import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";
import { Oval } from "react-loader-spinner";
import { Tooltip as ReactTooltip } from "react-tooltip";

// Style
import {
  MainWrapper,
  TopWrapper,
  MainWrapperFavorite,
} from "../../../style/DasboardMain";
import "react-tabs/style/react-tabs.css";

// Components
import Navigation from "../../../components/Navigation/Navigation";
import IntroText from "../../../components/introText/index";
import Search from "../../../components/inputs/Search";
import Breadcrumbs from "../../../components/breadcrumbs";
import Modal from "../../../components/modal/index";

// Images
import arrowIconUp from "../../../images/up.svg";
import arrowIconDown from "../../../images/down.svg";
import emptyState from "../../../images/contactLogo.svg";
import starOn from "../../../images/star_on.svg";
import starOff from "../../../images/star_off.svg";
import play from "../../../images/ic_play-button.svg";
import playBlue from "../../../images/play_blue.svg";

// Language
import {
  AvatarWithCountryContainer,
  BottomContent,
  Info,
  Stat,
  TeamCardWrap,
  TeamContent,
  TeamLeftInfo,
  TeamStatistics,
  TopContent,
} from "../team/Index";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ButtonLink from "../../../components/link/ButtonLink";
import {
  TabContent,
  TabListWrapper,
  TabsWrapper,
  TabTitle,
  TopList,
} from "../../../style/Favorites";
import Matches from "../../../components/table/Matches";
import Button from "../../../components/button";
import styled from "styled-components";
import {
  BottomCardsWrap,
  InnerContent,
  OverviewCard,
  StatsCard,
  TopCardInfo,
  TopCardsWrap,
  VideoContent,
  VideoInfo,
  VideoItems,
  VideoList,
  VideoStats,
  WrappBottomContent,
  WrappContent,
} from "../../../style/Overview";
import { breakpoints } from "../../../style/size";
import PlayerStat from "../../../components/table/PlayerStat";
import { EmptyCard, LoadingWrapper } from "../../../style/Global";
import { $apiV1, $apiV2 } from "../../../config/api";
import { playerGkEventsParams } from "../../../config/parametrs/events/playerGkEventsParams";
import { playerEventsParams } from "../../../config/parametrs/events/playerEventsParams";
import { useNotification } from "../../../components/notification/useNotification";
import { getPlayerParamMarkers } from "../../../helpers";
import { NotificationList } from "../../../components/notification/NotificationList";
import useOutsideClick from "../../../hooks/useOutsideClick";
import { useLexicon } from "../../../hooks/useLexicon";
import { AccessDenied, TeamPageContainer } from "../team/Index";
import { openVideoList } from "../../../helpers/service";
import { getUserProfile } from "../../../helpers/userProfile";
import {
  getGroupedAndSortedParametrs,
  getMediaUrl,
  getUniqueArrayObjectsByKey,
} from "../../../helpers";
import { useParameterLexicon } from "../../../hooks/useParameterLexicon";
import { useSavedFilterTemplates } from "../../../hooks/useSavedFilterTemplates";
import { Dropdown, DropdownButton, DropdownContent, DropdownItem, MatchDropdownItem, MatchCheckbox } from "../shared/ui/EventSharedStyles";
import {
  fetchShadowTeams,
  fetchShadowTeamById,
  addShadowTeamPlayer,
} from "../../../store/shadowTeam/shadowTeamService";
import {
  buildShadowTeamSlots,
  MAX_SHADOW_TEAM_PLAYERS_PER_SLOT,
  resolveShadowTeamAssignmentsBySlot,
} from "../../shadowTeam/utils/formation";
import {
  buildParameterGroupIdList,
  buildLexiconToParameterIdMap,
  findStatisticById,
  remapEventsParamsWithLiveIds,
  resolveStatisticByIdOrLexicon,
} from "./helpers/parameterResolution";
import {
  aggregatePlayerMatchStatistics,
  getStatisticParameterId,
  normalizeMarkerIds,
  resolveOverviewDisplayCount,
  resolveOverviewClickMarkerIds,
} from "./helpers/overviewVideoScope";
import {
  getPlayerPageTabHashByIndex,
  getPlayerPageTabIndexFromHash,
} from "./helpers/tabHash";
import {
  createMatchesCountOptions,
  deriveCompetitionsFromMatches,
  deriveSelectedMatches,
  getCompetitionSummaryLabel,
  getSeasonSummaryLabel,
  getSelectedMatchIds,
  reconcileCompetitionSelection,
} from "../team/helpers/pageFilters";
import { normalizeTemplateIds } from "../../../components/FilterMatches/templateFilters";
import {
  EMPTY_SELECTED_FILTERS,
  buildSelectedFiltersFromStoredGroups,
} from "./helpers/pageFilters";
import {
  buildSeasonSelectionQueryValue,
  normalizeSeasonSelectionIds,
  persistSeasonSelection,
  readStoredSeasonSelection,
} from "../shared/utils/seasonSelection";
import { trackEntityView } from "../../../tracking";

// Heavy tab panels are code-split and only loaded when their tab is opened.
// Statically importing them added their JS to the route chunk and, together with
// forceRenderTabPanel, mounted their whole subtrees on first paint — the main
// cause of the "page unresponsive" stall on this page.
const EventsPlayer = lazy(() => import("./EventsPlayer"));
const ShotsPlayer = lazy(() => import("./ShotsPlayer"));
const CrossesPlayer = lazy(() => import("./CrossesPlayer"));

export const TeamButtons = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  align-items: flex-end;

  @media only screen and (${breakpoints.mobile}) {
    width: 100%;
    align-items: stretch;
  }
`;

const ShadowTeamDropdownWrap = styled.div`
  position: relative;
`;

const ShadowTeamDropdownBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 0.5px solid ${({ theme }) => theme.borderColor};
  background: ${({ $open, theme }) =>
    $open ? theme.accentBgStrong : theme.accentBg};
  color: ${({ theme }) => theme.green};
  font-family: "SatoshiMedium";
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    background: ${({ theme }) => theme.accentBgStrong};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ShadowTeamPanel = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  background: ${({ theme }) => theme.dropdownBg};
  border: 0.5px solid ${({ theme }) => theme.dropdownBorder};
  border-radius: 10px;
  padding: 8px;
  min-width: 250px;
  z-index: 200;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
`;

const ShadowTeamPanelLabel = styled.p`
  font-size: 10px;
  color: ${({ theme }) => theme.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-family: "SatoshiMedium";
  padding: 4px 8px 8px;
  margin: 0;
`;

const ShadowTeamRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 7px;
  cursor: pointer;
  gap: 10px;
  transition: background 0.12s;

  &:hover {
    background: ${({ theme }) => theme.accentBg};
  }
`;

const ShadowTeamRowName = styled.div`
  font-size: 13px;
  font-family: "SatoshiMedium";
  color: ${({ theme }) => theme.textPrimary};
`;

const ShadowTeamRowMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.textMuted};
  margin-top: 2px;
`;

const ShadowTeamDivider = styled.div`
  border-top: 0.5px solid ${({ theme }) => theme.dropdownBorder};
  margin: 4px 0;
`;

const ShadowTeamNewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  color: ${({ theme }) => theme.green};
  font-size: 12px;
  font-family: "SatoshiMedium";
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s;

  &:hover {
    background: ${({ theme }) => theme.accentBg};
  }
`;

const ShadowTeamEmptyMsg = styled.div`
  padding: 10px 10px 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.textMuted};
  font-family: "SatoshiRegular";
  text-align: center;
`;

const ShadowTeamPanelBack = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
  font-family: "SatoshiMedium";
  width: 100%;
  text-align: left;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
  }
`;

const ShadowTeamPanelStepTitle = styled.div`
  font-size: 13px;
  font-family: "SatoshiMedium";
  color: ${({ theme }) => theme.textPrimary};
  padding: 0 8px 10px;
  border-bottom: 0.5px solid ${({ theme }) => theme.dropdownBorder};
  margin-bottom: 8px;
`;

const ShadowTeamSlotSection = styled.div`
  margin-bottom: 10px;
`;

const ShadowTeamSlotSectionLabel = styled.div`
  font-size: 9px;
  font-family: "SatoshiMedium";
  color: ${({ theme }) => theme.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 0 4px 5px;
`;

const ShadowTeamSlotGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 0 2px;
`;

const ShadowTeamSlotPill = styled.button`
  padding: 5px 11px;
  border-radius: 20px;
  border: 0.5px solid ${({ theme }) => theme.borderColor};
  background: ${({ theme }) => theme.cardBg};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 12px;
  font-family: "SatoshiMedium";
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.accentBorder};
    background: ${({ theme }) => theme.accentBg};
    color: ${({ theme }) => theme.green};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const PlayerHeaderFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;
  max-width: 520px;
  margin: 0 auto;

  @media only screen and (${breakpoints.mobile}) {
    max-width: 100%;
  }
`;

const PlayerHeaderFilterField = styled.div`
  min-width: 0;
`;

const PlayerHeaderFilterLabel = styled.span`
  display: block;
  margin-bottom: 4px;
  color: ${(props) => props.theme.blueish2};
  font-family: "SatoshiMedium";
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.8px;
  text-transform: uppercase;
`;

const PlayerHeaderDropdownButton = styled(DropdownButton)`
  width: 100%;
  min-height: 30px;
  font-size: 14px;
  line-height: 20px;
  padding-right: 20px;
  padding-bottom: 4px;

  img {
    width: 10px;
    height: 10px;
    right: 0;
    bottom: 8px;
  }
`;

const PlayerHeaderDropdownText = styled.span`
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ToolbarSpacer = styled.div`
  min-width: 0;
  flex: 1;

  @media only screen and (${breakpoints.mobile}) {
    width: 100%;
    min-width: 100%;
  }
`;

const areIdListsEqual = (firstList = [], secondList = []) => {
  if (firstList.length !== secondList.length) {
    return false;
  }

  return firstList.every((value, index) => value === secondList[index]);
};

const PLAYER_MULTI_SEASON_STORAGE_KEY = "season_player_selection";

const getInitialPlayerSelectedFilters = () => {
  try {
    const storedPlayerParameters = sessionStorage.getItem("playerParameters");
    if (!storedPlayerParameters) {
      return EMPTY_SELECTED_FILTERS;
    }

    const parsedPlayerParameters = JSON.parse(storedPlayerParameters);
    return buildSelectedFiltersFromStoredGroups(parsedPlayerParameters);
  } catch (error) {
    return EMPTY_SELECTED_FILTERS;
  }
};

const getInitialSelectedSeasonIds = () =>
  readStoredSeasonSelection({
    multiKey: PLAYER_MULTI_SEASON_STORAGE_KEY,
    legacyKey: "season_player",
  });

const buildPlayerStatisticsUrl = (playerId, seasonSelection) => {
  const seasonQueryValue = buildSeasonSelectionQueryValue(seasonSelection);

  return seasonQueryValue
    ? `/statistics/player_opt/${playerId}?year_season_id=${seasonQueryValue}`
    : `/statistics/player_opt/${playerId}`;
};

const buildPlayerMatchStatisticsUrl = ({
  playerId,
  seasonSelection,
  parameterIds = [],
  downloadType = "",
}) => {
  const seasonQueryValue = buildSeasonSelectionQueryValue(seasonSelection);
  const searchParams = [`params_ids=${parameterIds.join(",")}`];

  if (seasonQueryValue) {
    searchParams.unshift(`year_season_id=${seasonQueryValue}`);
  }

  if (downloadType) {
    searchParams.push(`format_data=${downloadType}`);
  }

  return `/platform/player/${playerId}/matches?${searchParams.join("&")}`;
};

const extractPlayerMatchRows = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  return Array.isArray(payload?.results) ? payload.results : [];
};

// "2025-2026" -> 2025. Used to rank multi-season responses so the newest one
// wins. Falsy/odd values sort last so a real season always beats them.
const getResponseSeasonStartYear = (response) => {
  const match = String(response?.year_season || "").match(/\d{4}/);
  return match ? Number(match[0]) : -Infinity;
};

const mergePlayerPageData = (responses = []) => {
  const validResponses = responses.filter(Boolean);
  if (validResponses.length === 0) {
    return null;
  }

  // player.team feeds the header "Current Club" and the breadcrumb, but it is
  // season-specific (a journeyman's team differs per season). When several
  // seasons are merged we must take the player block from the NEWEST season,
  // otherwise the current club reflects whichever season happened to be first
  // in the (unsorted) selection order — e.g. an old loan club — while the
  // merged matches_list shows the latest team. Mirrors primarySelectedSeasonId,
  // which also resolves to the newest selected season.
  const primaryResponse = validResponses.reduce((newest, candidate) =>
    getResponseSeasonStartYear(candidate) > getResponseSeasonStartYear(newest)
      ? candidate
      : newest
  );

  const mergedAvailableSeasons =
    getUniqueArrayObjectsByKey(
      responses.flatMap((response) =>
        Array.isArray(response?.available_seasons) ? response.available_seasons : []
      ),
      "id"
    ) || [];
  const mergedMatchesList =
    getUniqueArrayObjectsByKey(
      responses.flatMap((response) =>
        Array.isArray(response?.matches_list) ? response.matches_list : []
      ),
      "id"
    ) || [];

  return {
    ...primaryResponse,
    available_seasons:
      mergedAvailableSeasons.length > 0
        ? mergedAvailableSeasons
        : primaryResponse?.available_seasons,
    matches_list:
      mergedMatchesList.length > 0
        ? mergedMatchesList
        : primaryResponse?.matches_list,
  };
};

const mergePlayerMatchStatisticsPayloads = (responses = []) => {
  const primaryResponse = responses.find((response) => response !== undefined) ?? [];
  const mergedRows =
    getUniqueArrayObjectsByKey(
      responses.flatMap((response) => extractPlayerMatchRows(response)),
      "id"
    ) || [];

  if (Array.isArray(primaryResponse)) {
    return mergedRows;
  }

  return {
    ...(primaryResponse || {}),
    results: mergedRows,
  };
};

const Index = () => {
  const l = useLexicon();
  const { t } = useTranslation();
  const { getPlayerParameterLexiconId } = useParameterLexicon();
  const [players, setPlayers] = useState(null);
  const [getPlayerInfoStat, setGetPlayerInfoStat] = useState([]);
  const [playerMatches, setPlayerMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const isMobile = useMediaQuery({ query: `(${breakpoints.mobile})` });
  const [open, setOpen] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const [paramsShotList, setParamsShotList] = useState([]);
  const [paramsCrossesList, setParamsCrossesList] = useState([]);
  const [playerParameterIdByLexicon, setPlayerParameterIdByLexicon] = useState({});

  const [loadedSeasonSignature, setLoadedSeasonSignature] = useState("");
  const [openHeaderSeasonDropdown, setOpenHeaderSeasonDropdown] = useState(false);
  const [openHeaderCompetitionDropdown, setOpenHeaderCompetitionDropdown] = useState(false);
  const [openHeaderMatchesDropdown, setOpenHeaderMatchesDropdown] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState([]);
  const [hasCustomCompetitionSelection, setHasCustomCompetitionSelection] = useState(false);
  const [selectedMatchCount, setSelectedMatchCount] = useState(null);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [pendingAppliedTemplate, setPendingAppliedTemplate] = useState(null);
  const [tempSelectedFilters, setTempSelectedFilters] = useState(
    getInitialPlayerSelectedFilters
  );

  const { notifications, addNotification, removeNotification } =
    useNotification();

  const dispatch = useDispatch();
  const canDownloadReport = useSelector((state) => state.user.canDownloadReport);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const shadowTeams = useSelector((state) => state.shadowTeam.teams);
  const normalizedShadowTeams = Array.isArray(shadowTeams) ? shadowTeams : [];
  const addingToShadowTeam = useSelector(
    (state) => state.shadowTeam.loading.addPlayer
  );
  const [shadowTeamDropdownOpen, setShadowTeamDropdownOpen] = useState(false);
  const [shadowTeamSelectedTeamId, setShadowTeamSelectedTeamId] = useState(null);
  const [shadowTeamLoadingTeamId, setShadowTeamLoadingTeamId] = useState(null);

  const shadowTeamDropdownRef = useOutsideClick(() => {
    setShadowTeamDropdownOpen(false);
    setShadowTeamSelectedTeamId(null);
    setShadowTeamLoadingTeamId(null);
  });

  const handleOpenShadowTeamDropdown = () => {
    if (!shadowTeamDropdownOpen && normalizedShadowTeams.length === 0) {
      dispatch(fetchShadowTeams());
    }
    if (shadowTeamDropdownOpen) {
      setShadowTeamSelectedTeamId(null);
      setShadowTeamLoadingTeamId(null);
    }
    setShadowTeamDropdownOpen((prev) => !prev);
  };

  const handleSelectShadowTeam = async (team) => {
    const teamId = Number(team?.id);

    if (!Number.isFinite(teamId)) {
      return;
    }

    setShadowTeamSelectedTeamId(teamId);
    setShadowTeamLoadingTeamId(teamId);

    try {
      await dispatch(fetchShadowTeamById(teamId)).unwrap();
    } finally {
      setShadowTeamLoadingTeamId((currentValue) =>
        currentValue === teamId ? null : currentValue
      );
    }
  };

  const handleAddToShadowTeamSlot = async (slot) => {
    if (!selectedShadowTeam || isShadowTeamSlotFull(slot)) return;
    setShadowTeamDropdownOpen(false);
    const teamName = selectedShadowTeam.name;
    try {
      await dispatch(
        addShadowTeamPlayer({
          shadowTeamId: selectedShadowTeam.id,
          playerId: Number(id),
          positionGroup: slot.positionGroup,
          positionOrder: slot.positionOrder,
        })
      ).unwrap();
      addNotification(
        `Player added to "${teamName}" — ${slot.shortLabel}`,
        "success"
      );
      setShadowTeamSelectedTeamId(null);
      setShadowTeamLoadingTeamId(null);
    } catch (err) {
      addNotification(err || "Failed to add player to shadow team.", "error");
    }
  };

  const SHADOW_BAND_LABELS = {
    gk: "Goalkeeper",
    defense: "Defense",
    defensiveMid: "Defensive Mid",
    midfield: "Midfield",
    attackingMid: "Attacking Mid",
    attack: "Attack",
  };

  const selectedShadowTeam = useMemo(() => {
    const normalizedSelectedTeamId = Number(shadowTeamSelectedTeamId);
    if (!Number.isFinite(normalizedSelectedTeamId)) {
      return null;
    }

    return (
      normalizedShadowTeams.find(
        (team) => Number(team?.id) === normalizedSelectedTeamId
      ) ||
      null
    );
  }, [normalizedShadowTeams, shadowTeamSelectedTeamId]);

  const selectedShadowTeamHasLoadedAssignments = Array.isArray(
    selectedShadowTeam?.assignments
  );
  const isShadowTeamDetailLoading =
    shadowTeamLoadingTeamId !== null &&
    Number(shadowTeamLoadingTeamId) === Number(shadowTeamSelectedTeamId);

  const selectedShadowTeamSlotPlayers = useMemo(() => {
    if (!selectedShadowTeamHasLoadedAssignments) {
      return {};
    }

    return resolveShadowTeamAssignmentsBySlot(
      selectedShadowTeam?.assignments || [],
      selectedShadowTeam?.formation?.lines || []
    );
  }, [selectedShadowTeam, selectedShadowTeamHasLoadedAssignments]);

  const isShadowTeamSlotFull = useCallback(
    (slot) =>
      (selectedShadowTeamSlotPlayers?.[slot?.key] || []).length >=
      MAX_SHADOW_TEAM_PLAYERS_PER_SLOT,
    [selectedShadowTeamSlotPlayers]
  );

  const getShadowTeamSlotGroups = (formation) => {
    if (!Array.isArray(formation?.lines) || formation.lines.length === 0) {
      return [];
    }

    const slots = buildShadowTeamSlots(formation.lines);

    const byLine = {};
    slots.forEach((slot) => {
      const key = slot.lineIndex;
      if (!byLine[key]) {
        byLine[key] = { lineIndex: key, band: slot.band, slots: [] };
      }
      byLine[key].slots.push(slot);
    });
    return Object.values(byLine).sort((a, b) => {
      if (a.lineIndex === -1) return 1;
      if (b.lineIndex === -1) return -1;
      return b.lineIndex - a.lineIndex;
    });
  };

  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedTab = useMemo(
    () => getPlayerPageTabIndexFromHash(location.hash),
    [location.hash]
  );

  const [selectedSeasonIds, setSelectedSeasonIds] = useState(
    getInitialSelectedSeasonIds
  );
  const [overviewMarkerIdsByParamId, setOverviewMarkerIdsByParamId] = useState({});
  const showPageLoader = loading && !players;

  const replaceSelectedSeasons = useCallback((nextSeasonSelection) => {
    const normalizedSeasonIds = normalizeSeasonSelectionIds(nextSeasonSelection);

    if (normalizedSeasonIds.length === 0) {
      return;
    }

    persistSeasonSelection({
      multiKey: PLAYER_MULTI_SEASON_STORAGE_KEY,
      legacyKey: "season_player",
      seasonIds: normalizedSeasonIds,
    });

    setSelectedSeasonIds((previousSeasonIds) =>
      areIdListsEqual(previousSeasonIds, normalizedSeasonIds)
        ? previousSeasonIds
        : normalizedSeasonIds
    );
  }, []);

  const toggleSelectedSeason = useCallback((seasonId) => {
    const normalizedSeasonId = Number(seasonId);
    if (!Number.isFinite(normalizedSeasonId)) {
      return;
    }

    setSelectedSeasonIds((previousSeasonIds) => {
      const currentSeasonIds = normalizeSeasonSelectionIds(previousSeasonIds);
      const isSelected = currentSeasonIds.includes(normalizedSeasonId);
      const nextSeasonIds = isSelected
        ? currentSeasonIds.filter((item) => item !== normalizedSeasonId)
        : [...currentSeasonIds, normalizedSeasonId];

      if (nextSeasonIds.length === 0) {
        return previousSeasonIds;
      }

      persistSeasonSelection({
        multiKey: PLAYER_MULTI_SEASON_STORAGE_KEY,
        legacyKey: "season_player",
        seasonIds: nextSeasonIds,
      });

      return areIdListsEqual(currentSeasonIds, nextSeasonIds)
        ? previousSeasonIds
        : nextSeasonIds;
    });
  }, []);

  const availableSeasons = useMemo(
    () => (Array.isArray(players?.available_seasons) ? players.available_seasons : []),
    [players?.available_seasons]
  );

  const availableSeasonIds = useMemo(
    () => normalizeSeasonSelectionIds(availableSeasons.map((item) => item?.id)),
    [availableSeasons]
  );

  const isAllAvailableSeasonsSelected = useMemo(() => {
    if (availableSeasonIds.length === 0) {
      return false;
    }

    const selectedSeasonIdSet = new Set(
      normalizeSeasonSelectionIds(selectedSeasonIds)
    );

    return availableSeasonIds.every((seasonId) => selectedSeasonIdSet.has(seasonId));
  }, [availableSeasonIds, selectedSeasonIds]);

  const toggleAllAvailableSeasons = useCallback(() => {
    if (availableSeasonIds.length === 0) {
      return;
    }

    // Keep the existing invariant that at least one season is always selected.
    // A second click on "All seasons" restores the latest available season.
    replaceSelectedSeasons(
      isAllAvailableSeasonsSelected
        ? [availableSeasonIds[0]]
        : availableSeasonIds
    );
  }, [availableSeasonIds, isAllAvailableSeasonsSelected, replaceSelectedSeasons]);

  const selectedSeasonItems = useMemo(() => {
    if (availableSeasons.length === 0) {
      return [];
    }

    const selectedSeasonIdsSet = new Set(
      normalizeSeasonSelectionIds(selectedSeasonIds)
    );
    const matchedSelectedSeasons = normalizeSeasonSelectionIds(selectedSeasonIds)
      .map((seasonId) =>
        availableSeasons.find((item) => Number(item?.id) === Number(seasonId))
      )
      .filter(Boolean);

    if (matchedSelectedSeasons.length > 0) {
      return matchedSelectedSeasons;
    }

    const fallbackSelectedSeasons = availableSeasons.filter((item) =>
      selectedSeasonIdsSet.has(Number(item?.id))
    );
    if (fallbackSelectedSeasons.length > 0) {
      return fallbackSelectedSeasons;
    }

    return availableSeasons[0] ? [availableSeasons[0]] : [];
  }, [availableSeasons, selectedSeasonIds]);

  const primarySelectedSeasonId = useMemo(
    () => selectedSeasonItems[0]?.id ?? selectedSeasonIds[0] ?? "",
    [selectedSeasonIds, selectedSeasonItems]
  );

  const selectedSeasonQueryValue = useMemo(
    () => buildSeasonSelectionQueryValue(selectedSeasonIds),
    [selectedSeasonIds]
  );

  const availableMatches = useMemo(() => {
    const matchList = Array.isArray(players?.matches_list) ? players.matches_list : [];
    return [...matchList].sort((a, b) => new Date(b?.date) - new Date(a?.date));
  }, [players?.matches_list]);

  const competitions = useMemo(
    () => deriveCompetitionsFromMatches(availableMatches),
    [availableMatches]
  );

  const matchesCountList = useMemo(
    () => createMatchesCountOptions(availableMatches.length, l),
    [availableMatches.length, l]
  );

  const selectedMatchIds = useMemo(
    () => getSelectedMatchIds(selectedMatches),
    [selectedMatches]
  );

  const selectedHeaderSeasonLabel = useMemo(
    () => isAllAvailableSeasonsSelected
      ? t("playerReport.allSeasons")
      : getSeasonSummaryLabel(selectedSeasonItems, l(992)),
    [isAllAvailableSeasonsSelected, l, selectedSeasonItems, t]
  );
  const selectedHeaderCompetitionLabel = useMemo(
    () => getCompetitionSummaryLabel(selectedCompetition, l(993)),
    [l, selectedCompetition]
  );
  const selectedHeaderMatchesLabel = selectedMatchCount?.title || l(994);

  const isGoalkeeper = players?.player?.position?.[0]?.id === 10; // id goalkeeper position

  const baseParamsList = useMemo(() => {
    if (isGoalkeeper) {
      return playerGkEventsParams;
    }
    return playerEventsParams;
  }, [isGoalkeeper]);

  const paramsList = useMemo(() => {
    return remapEventsParamsWithLiveIds(baseParamsList, playerParameterIdByLexicon);
  }, [baseParamsList, playerParameterIdByLexicon]);

  const handleTabSelect = useCallback(
    (index) => {
      const nextHash = getPlayerPageTabHashByIndex(index);

      if (location.hash === nextHash) {
        return;
      }

      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: nextHash,
        },
        { replace: false }
      );
    },
    [location.hash, location.pathname, location.search, navigate]
  );

  const headerSeasonDropdownRef = useOutsideClick(() => {
    if (openHeaderSeasonDropdown) {
      setOpenHeaderSeasonDropdown(false);
    }
  });

  const headerCompetitionDropdownRef = useOutsideClick(() => {
    if (openHeaderCompetitionDropdown) {
      setOpenHeaderCompetitionDropdown(false);
    }
  });

  const headerMatchesDropdownRef = useOutsideClick(() => {
    if (openHeaderMatchesDropdown) {
      setOpenHeaderMatchesDropdown(false);
    }
  });

  useEffect(() => {
    let redirectTimeoutId = null;

    $apiV2.get('/access/check/' + id + '/player')
      .then(() => {
        setAccessDenied(false);
      })
      .catch((error) => {
        if (error?.response?.status === 403) {
          setAccessDenied(true);
          redirectTimeoutId = window.setTimeout(() => {
            navigate('/dashboard')
          }, 3000);
        } else {
          setAccessDenied(false);
        }
      });

    return () => {
      if (redirectTimeoutId) {
        window.clearTimeout(redirectTimeoutId);
      }
    };
  }, [id, navigate]);

  useEffect(() => {
    if (id) {
      setPendingAppliedTemplate(null);
      setHasCustomCompetitionSelection(false);
    }
  }, [id]);
  useEffect(() => {
    const availableSeasons = players?.available_seasons || [];
    if (availableSeasons.length === 0) {
      return;
    }

    // Skip reconciliation while data for the newly-selected seasons is still loading.
    // Otherwise the stale available_seasons (from the previous load) strips the
    // just-toggled season from selectedSeasonIds before the API returns.
    if (loadedSeasonSignature !== selectedSeasonQueryValue) {
      return;
    }

    const availableSeasonIdsSet = new Set(
      availableSeasons.map((item) => Number(item?.id))
    );
    const nextSelectedSeasonIds = normalizeSeasonSelectionIds(selectedSeasonIds).filter(
      (seasonId) => availableSeasonIdsSet.has(Number(seasonId))
    );

    // Only reset when no selected season appears in available seasons.
    // Avoids stripping a freshly-added season whose data hasn't loaded yet,
    // matching the defensive behaviour applied to the team page.
    const hasValidSelectedSeason = nextSelectedSeasonIds.length > 0;
    if (!hasValidSelectedSeason) {
      replaceSelectedSeasons([availableSeasons[0]?.id]);
    }
  }, [players?.available_seasons, replaceSelectedSeasons, selectedSeasonIds, loadedSeasonSignature, selectedSeasonQueryValue]);

  useEffect(() => {
    setOverviewMarkerIdsByParamId({});
  }, [id, selectedSeasonQueryValue]);

  useEffect(() => {
    setSelectedCompetition((previousCompetition) => {
      const nextCompetition = reconcileCompetitionSelection({
        availableCompetitions: competitions,
        selectedCompetitions: previousCompetition,
        hasCustomSelection: hasCustomCompetitionSelection,
      });

      const previousIds = normalizeTemplateIds(
        (Array.isArray(previousCompetition) ? previousCompetition : []).map(
          (item) => item?.competition_id
        )
      );
      const nextIds = normalizeTemplateIds(
        nextCompetition.map((item) => item?.competition_id)
      );

      if (areIdListsEqual(previousIds, nextIds)) {
        return previousCompetition;
      }

      return nextCompetition;
    });
  }, [competitions, hasCustomCompetitionSelection]);

  useEffect(() => {
    const allMatchesOption =
      matchesCountList.find((item) => Number(item?.id) === 1) ||
      matchesCountList[0] ||
      null;

    setSelectedMatchCount((previousMatchCount) => {
      if (!allMatchesOption) {
        return previousMatchCount;
      }

      const matchedOption = matchesCountList.find(
        (item) => Number(item?.id) === Number(previousMatchCount?.id)
      );
      const nextOption = matchedOption || allMatchesOption;
      const hasSameValue =
        Number(previousMatchCount?.id) === Number(nextOption?.id) &&
        Number(previousMatchCount?.value) === Number(nextOption?.value) &&
        previousMatchCount?.title === nextOption?.title;

      if (hasSameValue) {
        return previousMatchCount;
      }

      return nextOption;
    });
  }, [matchesCountList]);

  useEffect(() => {
    setSelectedMatches(
      deriveSelectedMatches({
        matches: availableMatches,
        selectedCompetition,
        selectedMatchCount,
      })
    );
  }, [availableMatches, selectedCompetition, selectedMatchCount]);

  const getPlayers = async (id, seasonArg) => {
    const seasonIdsToLoad = normalizeSeasonSelectionIds(
      seasonArg ?? selectedSeasonIds
    );
    const requestSeasonSelections =
      seasonIdsToLoad.length > 0 ? seasonIdsToLoad : [primarySelectedSeasonId].filter(Boolean);

    try {
      setLoading(true);
      const responses = await Promise.all(
        (requestSeasonSelections.length > 0 ? requestSeasonSelections : [null]).map(
          (seasonId) => $apiV1.get(buildPlayerStatisticsUrl(id, seasonId))
        )
      );
      const mergedPlayerData = mergePlayerPageData(
        responses.map((response) => response?.data)
      );
      const loadedSeasonIds =
        requestSeasonSelections.length > 0
          ? requestSeasonSelections
          : [primarySelectedSeasonId].filter(Boolean);
      const nextLoadedSeasonSignature = buildSeasonSelectionQueryValue(loadedSeasonIds);

      setPlayers(mergedPlayerData);
      trackEntityView({
        entity: mergedPlayerData?.player,
        entityId: id,
        entityType: "player",
        sourceSurface: "player_profile",
        metadata: {
          season_id: nextLoadedSeasonSignature || null,
          team_id: mergedPlayerData?.player?.team?.id,
          team_name: mergedPlayerData?.player?.team?.name,
        },
      });
      setIsFavorite(Boolean(mergedPlayerData?.in_favorites));
      setLoadedSeasonSignature(nextLoadedSeasonSignature);
    } catch (error) {
      console.error("Error fetching players:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPlayerMatches = async (id) => {
    try {
      setLoading(true);
      const response = await $apiV1.get(`/statistics/player_matches/${id}`);
      setPlayerMatches(response?.data);
    } catch (error) {
      console.error("Error fetching players:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPlayerNew = async (id, season, params = [], downloadType) => {
    try {
      let currentParams = Array.isArray(params) ? params : [];
      const seasonIdsToLoad = normalizeSeasonSelectionIds(
        season ?? selectedSeasonIds
      );
      const requestSeasonSelections =
        seasonIdsToLoad.length > 0 ? seasonIdsToLoad : [primarySelectedSeasonId].filter(Boolean);

      if (currentParams.length === 0) {
        if (
          Array.isArray(tempSelectedFilters?.checkedIds) &&
          tempSelectedFilters.checkedIds.length > 0
        ) {
          currentParams = [...tempSelectedFilters.checkedIds];
        } else if (sessionStorage.getItem("playerParameters")) {
          const storedParams = JSON.parse(sessionStorage.getItem("playerParameters"));
          currentParams = storedParams.flatMap((group) =>
            group[1].filter((item) => item.checked).map((item) => item.id)
          );
        }
      }

      if (downloadType) {
        const file = await $apiV2.get(
          buildPlayerMatchStatisticsUrl({
            playerId: id,
            seasonSelection:
              requestSeasonSelections.length > 0
                ? requestSeasonSelections
                : season,
            parameterIds: currentParams,
            downloadType,
          })
        );
        if (downloadType === 'xml') {
          fetch(file.data.file_url)
            .then(response => {
              if (!response.ok) throw new Error('Network response was not ok');
              return response.blob();
            })
            .then(blob => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = file.data.file_url.split("/")[
                file.data.file_url.split("/").length - 1
              ];
              a.style.display = 'none';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            });
          return
        } else {
          const a = document.createElement("a");
          a.href = file.data.file_url;
          a.download =
            file.data.file_url.split("/")[
            file.data.file_url.split("/").length - 1
            ];
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return
        }
      } else {
        setLoading(true);
        const responses = await Promise.all(
          (
            requestSeasonSelections.length > 0 ? requestSeasonSelections : [null]
          ).map((seasonId) =>
            $apiV2.get(
              buildPlayerMatchStatisticsUrl({
                playerId: id,
                seasonSelection: seasonId,
                parameterIds: currentParams,
              })
            )
          )
        );
        setGetPlayerInfoStat(
          mergePlayerMatchStatisticsPayloads(
            responses.map((response) => response?.data)
          )
        );
      }
    } catch (error) {
      console.error("Error fetching players:", error);
      if (downloadType) {
        addNotification(`Error in receiving ${downloadType} file.`, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async () => {
    try {
      if (isFavorite) {
        await $apiV1.delete(`/users/favorites/players/${id}`);
      } else {
        await $apiV1.post(`/users/favorites/players`, { player: id });
      }
      setIsFavorite((prev) => !prev);
    } catch (error) {
      console.error("Error toggling favorite status:", error);
    }
  };

  const selectedParameterIds = useMemo(
    () => normalizeTemplateIds(tempSelectedFilters?.checkedIds || []),
    [tempSelectedFilters?.checkedIds]
  );
  const selectedParameterIdsSignature = selectedParameterIds.join(",");

  const applyPlayerParametersSelection = useCallback(
    (parameterGroups = [], parameterIds = []) => {
      const normalizedParameterIds = normalizeTemplateIds(parameterIds);
      const selectedIdSet = new Set(normalizedParameterIds);

      const nextGroups = parameterGroups.map((group) => [
        group?.[0],
        (group?.[1] || []).map((parameter) => ({
          ...parameter,
          checked: selectedIdSet.has(Number(parameter?.id)),
        })),
      ]);

      sessionStorage.setItem("playerParameters", JSON.stringify(nextGroups));

      const checkedParams = nextGroups.flatMap((group) =>
        (group?.[1] || []).filter((parameter) => parameter?.checked)
      );
      const checkedIds = checkedParams.map((parameter) => Number(parameter?.id));

      setTempSelectedFilters((prevState) => {
        const previousIds = normalizeTemplateIds(prevState?.checkedIds || []);
        if (areIdListsEqual(previousIds, checkedIds)) {
          return prevState;
        }

        return {
          checkedIds,
          params: checkedParams,
        };
      });
    },
    []
  );

  const handlePlayerParametersChange = useCallback(
    (parameterGroups = []) => {
      const checkedParams = parameterGroups.flatMap((group) =>
        (group?.[1] || []).filter((parameter) => parameter?.checked)
      );
      const checkedIds = checkedParams.map((parameter) => Number(parameter?.id));

      sessionStorage.setItem("playerParameters", JSON.stringify(parameterGroups));
      setTempSelectedFilters((prevState) => {
        const previousIds = normalizeTemplateIds(prevState?.checkedIds || []);
        if (areIdListsEqual(previousIds, checkedIds)) {
          return prevState;
        }

        return {
          checkedIds,
          params: checkedParams,
        };
      });
    },
    []
  );

  const applyTemplateParameterIds = useCallback(
    async (parameterIds = []) => {
      const normalizedParameterIds = normalizeTemplateIds(parameterIds);

      let parameterGroups = null;
      if (sessionStorage.getItem("playerParameters")) {
        parameterGroups = JSON.parse(sessionStorage.getItem("playerParameters"));
      }

      if (!parameterGroups) {
        const response = await $apiV1.get("/stats/parameters/player/", {
          params: {
            page_size: 1000,
          },
        });
        const data = response?.data?.results || [];
        parameterGroups = getGroupedAndSortedParametrs(data, true);
      }

      if (!Array.isArray(parameterGroups)) {
        return;
      }

      applyPlayerParametersSelection(parameterGroups, normalizedParameterIds);
    },
    [applyPlayerParametersSelection]
  );

  const handleApplyTemplate = useCallback((filters) => {
    setPendingAppliedTemplate({
      requestId: Date.now(),
      filters: filters && typeof filters === "object" ? filters : {},
    });
  }, []);

  const templateFilters = useMemo(
    () => ({
      seasonIds: selectedSeasonIds.map((item) => Number(item)),
      competitionIds: selectedCompetition.map((item) =>
        Number(item?.competition_id)
      ),
      matchCountId: selectedMatchCount?.id ?? null,
      matchIds: selectedMatchIds.map((item) => Number(item)),
      parameterIds: selectedParameterIds,
    }),
    [
      selectedCompetition,
      selectedMatchCount,
      selectedMatchIds,
      selectedParameterIds,
      selectedSeasonIds,
    ]
  );

  const savedTemplates = useSavedFilterTemplates({
    pageKey: "player_stats",
    filters: templateFilters,
    onApplyTemplate: handleApplyTemplate,
    showUpdateButton: false,
  });

  useEffect(() => {
    if (!pendingAppliedTemplate) {
      return;
    }

    let isCancelled = false;

    const hydrateTemplateFilters = async () => {
      const appliedFilters = pendingAppliedTemplate?.filters || {};

      const seasonIds = normalizeTemplateIds(appliedFilters?.seasonIds);
      const templateSeasonIds =
        seasonIds.length > 0
          ? availableSeasons
              .filter((item) => seasonIds.includes(Number(item?.id)))
              .map((item) => Number(item?.id))
          : [];

      if (
        templateSeasonIds.length > 0 &&
        !areIdListsEqual(
          normalizeSeasonSelectionIds(selectedSeasonIds),
          templateSeasonIds
        )
      ) {
        replaceSelectedSeasons(templateSeasonIds);
        return;
      }

      if (
        templateSeasonIds.length > 0 &&
        loadedSeasonSignature !==
          buildSeasonSelectionQueryValue(templateSeasonIds)
      ) {
        return;
      }

      if (Object.prototype.hasOwnProperty.call(appliedFilters, "competitionIds")) {
        setHasCustomCompetitionSelection(true);
        const competitionIds = normalizeTemplateIds(appliedFilters.competitionIds);
        if (competitionIds.length === 0) {
          setSelectedCompetition([]);
        } else {
          const competitionIdSet = new Set(competitionIds);
          const nextCompetitions = competitions.filter((competitionItem) =>
            competitionIdSet.has(Number(competitionItem?.competition_id))
          );
          setSelectedCompetition(
            nextCompetitions.length > 0 ? nextCompetitions : competitions
          );
        }
      }

      if (Object.prototype.hasOwnProperty.call(appliedFilters, "matchCountId")) {
        const requestedMatchCountId = Number(appliedFilters.matchCountId);
        const fallbackOption =
          matchesCountList.find((item) => Number(item?.id) === 1) ||
          matchesCountList[0] ||
          null;
        const nextMatchCount =
          matchesCountList.find(
            (item) => Number(item?.id) === requestedMatchCountId
          ) || fallbackOption;
        setSelectedMatchCount(nextMatchCount);
      }

      if (Object.prototype.hasOwnProperty.call(appliedFilters, "parameterIds")) {
        try {
          await applyTemplateParameterIds(appliedFilters.parameterIds || []);
        } catch (error) {
          console.error("Error applying player template parameters:", error);
        }
      }

      if (!isCancelled) {
        setPendingAppliedTemplate(null);
      }
    };

    hydrateTemplateFilters();

    return () => {
      isCancelled = true;
    };
  }, [
    applyTemplateParameterIds,
    availableSeasons,
    competitions,
    loadedSeasonSignature,
    matchesCountList,
    pendingAppliedTemplate,
    replaceSelectedSeasons,
    selectedSeasonIds,
  ]);

  const getShotParams = async () => {
    try {
      const response = await $apiV1.get(`/stats/parameters/player`, {
        params: {
          group: 2,
          page_size: 1000,
        },
      });
      const shotParams = response?.data?.results || [];
      setParamsShotList(shotParams);
      const nextMap = buildLexiconToParameterIdMap(
        shotParams,
        getPlayerParameterLexiconId
      );
      if (Object.keys(nextMap).length > 0) {
        setPlayerParameterIdByLexicon((prev) => ({
          ...prev,
          ...nextMap,
        }));
      }
    } catch (error) {
      console.error("Error fetching players:", error);
    } finally {
    }
  };

  const getCrossesParams = async () => {
    try {
      const response = await $apiV1.get(`/stats/parameters/player`, {
        params: {
          group: 3,
          page_size: 1000,
        },
      });
      const crossesParams = response?.data?.results || [];
      setParamsCrossesList(crossesParams);
      const nextMap = buildLexiconToParameterIdMap(
        crossesParams,
        getPlayerParameterLexiconId
      );
      if (Object.keys(nextMap).length > 0) {
        setPlayerParameterIdByLexicon((prev) => ({
          ...prev,
          ...nextMap,
        }));
      }
    } catch (error) {
      console.error("Error fetching players:", error);
    } finally {
    }
  };

  const getPlayerParameters = useCallback(async () => {
    try {
      const response = await $apiV1.get(`/stats/parameters/player/`, {
        params: {
          page_size: 1000,
        },
      });
      const nextMap = buildLexiconToParameterIdMap(
        response?.data?.results || [],
        getPlayerParameterLexiconId
      );
      if (Object.keys(nextMap).length > 0) {
        setPlayerParameterIdByLexicon((prev) => ({
          ...prev,
          ...nextMap,
        }));
      }
    } catch (error) {
      console.error("Error fetching player parameters:", error);
    } finally {
    }
  }, []);

  const getEventParameterMappings = useCallback(async () => {
    const groupIds = buildParameterGroupIdList(baseParamsList);
    if (groupIds.length === 0) {
      return;
    }

    try {
      const responses = await Promise.all(
        groupIds.map((groupId) =>
          $apiV1.get(`/stats/parameters/player`, {
            params: {
              group: groupId,
              page_size: 1000,
            },
          })
        )
      );

      const nextMap = buildLexiconToParameterIdMap(
        responses.flatMap((response) => response?.data?.results || []),
        getPlayerParameterLexiconId
      );

      if (Object.keys(nextMap).length > 0) {
        setPlayerParameterIdByLexicon((prev) => ({
          ...prev,
          ...nextMap,
        }));
      }
    } catch (error) {
      console.error("Error fetching player event parameter mappings:", error);
    } finally {
    }
  }, [baseParamsList, getPlayerParameterLexiconId]);

  const [userDetail, setUserDetail] = useState("");

  const getUser = async () => {
    try {
      const response = await getUserProfile();
      setUserDetail(response?.data?.user);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    getPlayers(id, selectedSeasonIds);
  }, [id, selectedSeasonQueryValue]);

  useEffect(() => {
    getPlayerNew(id, selectedSeasonIds, selectedParameterIds);
  }, [id, selectedParameterIdsSignature, selectedSeasonQueryValue]);

  useEffect(() => {
    getPlayerMatches(id);
    getShotParams();
    getCrossesParams();
    getUser();
    getPlayerParameters();
  }, [id, getPlayerParameters]);

  useEffect(() => {
    // Some event groups, including set pieces, are not always present in the
    // generic parameter payload, so we backfill them from their exact groups.
    getEventParameterMappings();
  }, [getEventParameterMappings]);

  const breadcrumbs = [
    {
      title: l(1),
      link: "/tournaments",
    },
    {
      title: `${players?.player?.team?.name}`,
      link: `/tournaments/team/${players?.player?.team?.id}`,
    },
    {
      title: `${players?.player?.display_name}`,
    },
  ];

  const [playerInfo, setPlayerInfo] = useState();
  const handleInitiate = (players) => {
    setPlayerInfo(players?.player);
    setOpen(!open);
  };

  const handleDownloadPlayerReport = async () => {
    if (!id || isDownloadingReport) return;
    const notifId = 'downloading-player-report';
    setIsDownloadingReport(true);
    addNotification('Download in progress...', 'info', { id: notifId, duration: 0 });
    try {
      const activeCompetitions = selectedCompetition.length > 0 ? selectedCompetition : competitions;
      const competitionIds = [...new Set(activeCompetitions.map(item => item.competition_id).filter(Boolean))];
      // The header supports selecting multiple year seasons.  Send the whole
      // selection so the exported report represents the same data as the page.
      const yearSeasonIds = normalizeSeasonSelectionIds(selectedSeasonIds);

      const isAllMatches = !selectedMatchCount || Number(selectedMatchCount?.id) === 1;
      let dateFrom = null;
      let dateTo = null;
      if (!isAllMatches && selectedMatches.length > 0) {
        const dates = selectedMatches.map(m => m.date).filter(Boolean).sort();
        dateFrom = dates[0]?.slice(0, 10) ?? null;
        dateTo = dates[dates.length - 1]?.slice(0, 10) ?? null;
      }

      const response = await $apiV2.post(
        `/platform/player/${id}/export/report/pdf`,
        {
          competition_ids: competitionIds,
          year_season_ids: yearSeasonIds,
          date_from: dateFrom,
          date_to: dateTo,
        },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = isAllAvailableSeasonsSelected
        ? `player-report-${id}-all-seasons.pdf`
        : `player-report-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Player report download failed', e);
      addNotification('Error, please try again later.', 'error');
    } finally {
      removeNotification(notifId);
      setIsDownloadingReport(false);
    }
  };

  const handleSendEmail = (playerInfo) => {
    const subject = encodeURIComponent(playerInfo.display_name);
    const mailtoLink = `mailto:sales@smrtstats.com?subject=${subject}`;
    window.location.href = mailtoLink;
  };

  const handleScreenShot = async () => {
    try {
      // Load html2canvas on demand so its ~150KB stay out of the initial route
      // chunk; it is only needed when the user explicitly takes a screenshot.
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(document.body);
      const screenshot = canvas.toDataURL("image/png");

      const link = document.createElement("a");
      link.href = screenshot;
      link.download = "screenshot.png";
      link.click();
    } catch (error) {
      console.error("Error capturing screenshot:", error);
    }
  };

  const getParameterDisplayName = useCallback(
    (parameterInput) => {
      const fallbackName =
        typeof parameterInput === "string"
          ? parameterInput
          : parameterInput?.parameter_name || parameterInput?.name || "";
      if (!fallbackName) return "";
      const lexiconId = getPlayerParameterLexiconId(parameterInput);
      if (lexiconId) {
        return l(lexiconId) || fallbackName;
      }
      return fallbackName;
    },
    [getPlayerParameterLexiconId, l]
  );

  const isCanClickPlayerStat = (parameter) => {
    const parameterName = parameter?.parameter_name || parameter?.name || "";
    const metricCount = Number(parameter?.count ?? 0);
    const hasCount = Number.isFinite(metricCount) ? metricCount > 0 : Boolean(parameter?.count);
    const localMarkerIds = normalizeMarkerIds(parameter?.markers);
    const hasLocalMarkers = localMarkerIds.length > 0;
    const parameterId = getStatisticParameterId(parameter);

    return (
      !parameter ||
      (!hasCount && !hasLocalMarkers) ||
      parameter?.isMatchesPlayed ||
      (parameterId === null && !hasLocalMarkers) ||
      parameterName.includes("%") ||
      parameterName.includes("Mins Played")
    );
  };

  const [successPopup, setSuccessPopup] = useState(false);
  const modal = (
    <>
      <Modal isOpen={open} setIsOpen={setOpen} closeIc>
        <h1>{l(1080)}</h1>
        <p>
          {/* Someone from{" "}
          <span style={{ color: "#BFE82D", textDecoration: "underline" }}>
            Smrt Stats
          </span>{" "}
          team will initate contact between you and player{" "} */}
          {l(1081)}
          {playerInfo?.display_name}. {l(1082)}
        </p>
        <Button
          title="confirm"
          size={"full"}
          style={{ marginTop: "30px" }}
          onClick={() => handleSendEmail(playerInfo)}
        />
      </Modal>

      {/* Success added to compare  */}
      <Modal isOpen={successPopup} setIsOpen={setSuccessPopup} closeIc>
        <h1>{l(1046)}</h1>
        <br />
        <Button
          title={l(1047)}
          size={"full"}
          onClick={() => navigate(`/compare`)}
        />
      </Modal>
    </>
  );

  const handleCompare = async () => {
    // Convert id to number (assuming id is initially a string)
    const playerId = parseInt(id, 10);

    try {
      // Call API to add player to compare list
      await $apiV2.post(`/platform/compare/add`, {
        player_id: playerId,
      });

      // Update compare_workspace_draft_v1 in localStorage (the format the Compare page reads)
      const COMPARE_WORKSPACE_DRAFT_STORAGE_KEY = "compare_workspace_draft_v1";
      let draft = { selectedCompareParams: [], players: [] };

      try {
        const raw = localStorage.getItem(COMPARE_WORKSPACE_DRAFT_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            draft = {
              selectedCompareParams: Array.isArray(parsed.selectedCompareParams)
                ? parsed.selectedCompareParams
                : [],
              players: Array.isArray(parsed.players) ? parsed.players : [],
            };
          }
        }
      } catch (e) {
        // ignore parse errors, start with empty draft
      }

      // Prevent duplicate players
      const alreadyExists = draft.players.some(
        (p) => Number(p.playerId ?? p.player_id ?? p.id) === playerId
      );

      if (!alreadyExists) {
        draft.players.push({ playerId, yearSeasonId: null });
        localStorage.setItem(
          COMPARE_WORKSPACE_DRAFT_STORAGE_KEY,
          JSON.stringify(draft)
        );
      }

      setSuccessPopup(!successPopup);
    } catch (error) {
      console.error("Error adding player to compare list:", error);
    }
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat("en-US").format(number);
  };

  const aggregatedMatchStatistics = useMemo(() => {
    const allMatches = Array.isArray(getPlayerInfoStat)
      ? getPlayerInfoStat
      : Array.isArray(getPlayerInfoStat?.results)
        ? getPlayerInfoStat.results
        : [];

    if (selectedMatchIds.length === 0) {
      return [];
    }

    const selectedIdSet = new Set(selectedMatchIds.map((item) => Number(item)));
    const scopedMatches = allMatches.filter((match) =>
      selectedIdSet.has(Number(match?.id))
    );

    return aggregatePlayerMatchStatistics(scopedMatches);
  }, [getPlayerInfoStat, selectedMatchIds]);

  const getParameterById = useCallback((parameterId) => {
    return findStatisticById(aggregatedMatchStatistics, parameterId);
  }, [aggregatedMatchStatistics]);

  const formatStatCount = (value) => {
    if (value === null || value === undefined) return value;
    if (typeof value !== "number") return value;
    return Number.isInteger(value) ? formatNumber(value) : value.toFixed(1);
  };

  const cacheOverviewMetricMarkers = useCallback(
    async (parameterId, markerIds) => {
      if (parameterId === null || !Array.isArray(markerIds) || markerIds.length === 0) {
        return;
      }

      setOverviewMarkerIdsByParamId((prev) => {
        const previousMarkers = prev[parameterId];
        const hasSameMarkers =
          Array.isArray(previousMarkers) &&
          previousMarkers.length === markerIds.length &&
          previousMarkers.every((item, index) => String(item) === String(markerIds[index]));

        if (hasSameMarkers) {
          return prev;
        }

        return {
          ...prev,
          [parameterId]: markerIds,
        };
      });
    },
    []
  );

  const canOpenPlayerStat = (parameter) => !isCanClickPlayerStat(parameter);
  const getOverviewMetricDisplayCount = (parameter) => {
    if (!canOpenPlayerStat(parameter)) {
      return parameter?.count;
    }

    return resolveOverviewDisplayCount({ parameter });
  };

  const openPlayerStatVideos = async (
    parameter,
    newTab = false,
    labelConfig = null
  ) => {
    if (!canOpenPlayerStat(parameter)) return;

    const displayName = labelConfig
      ? resolveBottomLabel(labelConfig, getParameterDisplayName(parameter))
      : getParameterDisplayName(parameter);
    const parameterId = getStatisticParameterId(parameter);
    const localMarkerIds = normalizeMarkerIds(parameter?.markers);
    const cachedMarkerIds =
      parameterId !== null
        ? normalizeMarkerIds(overviewMarkerIdsByParamId?.[parameterId] || [])
        : [];
    const title = `${displayName} - ${players?.player?.display_name}`;
    const metricCount = Number(parameter?.count ?? 0);
    const hasMetricCount = Number.isFinite(metricCount) && metricCount > 0;

    if (parameterId === null) {
      if (localMarkerIds.length > 0) {
        openVideoList(localMarkerIds.join(","), title, newTab);
      }
      return;
    }

    if (cachedMarkerIds.length > 0) {
      openVideoList(cachedMarkerIds.join(","), title, newTab);
      return;
    }

    const canUseLocalFastPath =
      localMarkerIds.length > 0 &&
      (!hasMetricCount || localMarkerIds.length >= metricCount);

    if (canUseLocalFastPath) {
      cacheOverviewMetricMarkers(parameterId, localMarkerIds);
      openVideoList(localMarkerIds.join(","), title, newTab);
      return;
    }

    try {
      document.body.classList.add("loading-cursor");
      const data = await getPlayerParamMarkers(
        id,
        parameterId,
        selectedSeasonIds
      );
      const { markerIds } = resolveOverviewClickMarkerIds({
        fetchedResults: data?.results,
      });

      if (markerIds.length > 0) {
        cacheOverviewMetricMarkers(parameterId, markerIds);
        openVideoList(markerIds.join(","), title, newTab);
        return;
      }

      addNotification(
        `There are 0 clips in the response from the server.`,
        "warning"
      );
    } catch (e) {
      console.log("error get scoped player markers", e);
      const { markerIds } = resolveOverviewClickMarkerIds({
        localMarkers: localMarkerIds,
        useLocalFallback: true,
      });

      if (markerIds.length > 0) {
        cacheOverviewMetricMarkers(parameterId, markerIds);
        addNotification(
          `Unable to load full season clips. Opening available clips instead.`,
          "warning"
        );
        openVideoList(markerIds.join(","), title, newTab);
        return;
      }

      addNotification(`Error in receiving player clips.`, "error");
    } finally {
      document.body.classList.remove("loading-cursor");
    }
  };

  const resolveBottomLabel = useCallback((labelConfig, fallbackValue = "") => {
    if (!labelConfig) return fallbackValue;

    if (labelConfig.preferredText) {
      const preferredLexiconId = getPlayerParameterLexiconId(labelConfig.preferredText);
      if (preferredLexiconId) {
        const preferredText = l(preferredLexiconId);
        if (preferredText) return preferredText;
      }
    }

    if (labelConfig.preferredLexiconId) {
      const preferredText = l(labelConfig.preferredLexiconId);
      if (preferredText) return preferredText;
    }

    if (labelConfig.fallbackLexiconId) {
      const fallbackText = l(labelConfig.fallbackLexiconId);
      if (fallbackText) return fallbackText;
    }

    if (labelConfig.fallbackText) return labelConfig.fallbackText;

    return fallbackValue;
  }, [getPlayerParameterLexiconId, l]);

  const getBottomMetricLabel = useCallback(
    (metric, labelConfig) =>
      resolveBottomLabel(labelConfig, getParameterDisplayName(metric)),
    [getParameterDisplayName, resolveBottomLabel]
  );

  const overviewStatsDisplayItems = useMemo(() => {
    if (!Array.isArray(aggregatedMatchStatistics)) {
      return [];
    }

    const matchesPlayed = {
      parameter_id: "matches_played",
      parameter_name: l(325) || "Matches Played",
      count: selectedMatchIds.length,
      isMatchesPlayed: true,
    };
    const translatedNameCounts = {};

    return [matchesPlayed, ...aggregatedMatchStatistics].map((parameter) => {
      if (parameter.isMatchesPlayed) {
        return {
          parameter,
          displayName: parameter.parameter_name,
        };
      }

      const translatedName = getParameterDisplayName(parameter);
      const fallbackName =
        parameter?.parameter_name || parameter?.name || translatedName;
      const normalizedTranslatedName = String(translatedName || "")
        .trim()
        .toLowerCase();

      translatedNameCounts[normalizedTranslatedName] =
        (translatedNameCounts[normalizedTranslatedName] || 0) + 1;

      const shouldUseFallbackName =
        normalizedTranslatedName &&
        translatedNameCounts[normalizedTranslatedName] > 1 &&
        fallbackName &&
        fallbackName !== translatedName;

      return {
        parameter,
        displayName: shouldUseFallbackName ? fallbackName : translatedName,
      };
    });
  }, [aggregatedMatchStatistics, getParameterDisplayName, l, selectedMatchIds.length]);

  const overviewVideoSections = useMemo(() => {
    const buildMetric = (parameterId, labelConfig) => {
      const existingMetric = resolveStatisticByIdOrLexicon({
        statistics: aggregatedMatchStatistics,
        parameterId,
        labelConfig,
        getPlayerParameterLexiconId,
        lexiconToParameterIdMap: playerParameterIdByLexicon,
      });
      if (existingMetric) return existingMetric;

      const aggregatedMetric = resolveStatisticByIdOrLexicon({
        statistics: aggregatedMatchStatistics,
        parameterId,
        labelConfig,
        getPlayerParameterLexiconId,
        lexiconToParameterIdMap: playerParameterIdByLexicon,
      });
      if (aggregatedMetric) {
        return {
          ...aggregatedMetric,
          markers: Array.isArray(aggregatedMetric?.markers)
            ? aggregatedMetric.markers
            : [],
        };
      }

      const placeholderLabel = resolveBottomLabel(labelConfig, "");

      return {
        parameter_id: parameterId,
        parameter_name: placeholderLabel,
        name: placeholderLabel,
        count: 0,
        markers: [],
      };
    };

    const buildCard = ({
      key,
      tone,
      primaryId,
      secondaryIds = [],
      primaryLabel,
      secondaryLabels = {},
    }) => {
      const primaryMetric = buildMetric(primaryId, primaryLabel);

      const secondaryMetrics = secondaryIds
        .map((parameterId) => buildMetric(parameterId, secondaryLabels?.[parameterId]));

      return {
        key,
        tone,
        group: [primaryMetric, ...secondaryMetrics],
        labels: {
          primary: primaryLabel,
          secondaryById: secondaryLabels,
        },
      };
    };

    if (isGoalkeeper) {
      return [
        {
          key: "gk-attack",
          titleLabel: {
            preferredText: "Offensive Actions",
            fallbackText: "Offensive Actions",
          },
          cards: [
            buildCard({
              key: "gk-saved-shots",
              tone: "attack",
              primaryId: 174,
              secondaryIds: [186, 160, 196],
              primaryLabel: {
                preferredText: "Saved Shots",
                fallbackLexiconId: 772,
              },
              secondaryLabels: {
                186: { preferredText: "Hand Passes", fallbackLexiconId: 782 },
                160: { preferredText: "Clearances", fallbackLexiconId: 641 },
                196: { preferredText: "Bouncing Saves", fallbackLexiconId: 773 },
              },
            }),
            buildCard({
              key: "gk-on-ball-events",
              tone: "attack",
              primaryId: 14,
              secondaryIds: [123, 158, 174],
              primaryLabel: {
                preferredText: "On Ball Events",
                fallbackLexiconId: 326,
              },
              secondaryLabels: {
                123: { preferredLexiconId: 611, fallbackText: "Duels +" },
                158: { preferredText: "Ball Recoveries", fallbackLexiconId: 337 },
                174: { preferredText: "Saved Shots", fallbackLexiconId: 772 },
              },
            }),
          ],
        },
        {
          key: "gk-defense",
          titleLabel: {
            preferredText: "Defensive Actions",
            fallbackText: "Defensive Actions",
          },
          cards: [
            buildCard({
              key: "gk-goals-conceded",
              tone: "defense",
              primaryId: 173,
              secondaryIds: [128, 21, 23],
              primaryLabel: {
                preferredText: "Goals Conceded",
                fallbackLexiconId: 318,
              },
              secondaryLabels: {
                128: { preferredLexiconId: 615, fallbackText: "Aerial Duels +" },
                21: { preferredText: "Yellow Cards", fallbackLexiconId: 104 },
                23: { preferredText: "Red Cards", fallbackLexiconId: 103 },
              },
            }),
            buildCard({
              key: "gk-goal-mistakes",
              tone: "defense",
              primaryId: 161,
              secondaryIds: [20, 19, 128],
              primaryLabel: {
                preferredText: "Goals Mistakes",
                fallbackLexiconId: 1331,
              },
              secondaryLabels: {
                20: { preferredText: "Fouls Suffered", fallbackLexiconId: 554 },
                19: { preferredText: "Fouls Made", fallbackLexiconId: 328 },
                128: { preferredLexiconId: 615, fallbackText: "Aerial Duels +" },
              },
            }),
          ],
        },
      ];
    }

    const defensiveMistakesMetric =
      getParameterById(161) ||
      getParameterById(162) ||
      buildMetric(161, {
        preferredText: "Goal Mistakes",
        fallbackLexiconId: 324,
      });

    return [
      {
        key: "outfield-offensive",
        titleLabel: {
          preferredText: "Offensive Actions",
          fallbackText: "Offensive Actions",
        },
        cards: [
          buildCard({
            key: "of-goals",
            tone: "attack",
            primaryId: 2,
            secondaryIds: [14, 201, 24, 36],
            primaryLabel: {
              preferredText: "Goals",
              fallbackLexiconId: 424,
            },
            secondaryLabels: {
              14: { preferredText: "On Ball Events", fallbackLexiconId: 326 },
              201: { preferredText: "Assists", fallbackLexiconId: 105 },
              24: { preferredText: "Shots", fallbackLexiconId: 18 },
              36: { preferredLexiconId: 578, fallbackText: "Passes +" },
            },
          }),
          buildCard({
            key: "of-goal-scoring-opportunities",
            tone: "attack",
            primaryId: 4,
            secondaryIds: [46, 113, 50, 25],
            primaryLabel: {
              preferredText: "Goal Scoring Opportunities",
              fallbackLexiconId: 560,
            },
            secondaryLabels: {
              46: { preferredLexiconId: 598, fallbackText: "Key Pass +" },
              113: { preferredLexiconId: 649, fallbackText: "Dribbles +" },
              50: { preferredLexiconId: 587, fallbackText: "Crosses" },
              25: { preferredLexiconId: 462, fallbackText: "Shots on Target" },
            },
          }),
        ],
      },
      {
        key: "outfield-defense",
        titleLabel: {
          preferredText: "Defensive Actions",
          fallbackText: "Defensive Actions",
        },
        cards: [
          {
            key: "of-defensive-actions",
            tone: "defense",
            group: [
              buildMetric(122, {
                preferredText: "Duels",
                fallbackLexiconId: 20,
              }),
              buildMetric(152, {
                preferredText: "Interceptions",
                fallbackLexiconId: 635,
              }),
              buildMetric(143, {
                preferredText: "Tackles Won",
                fallbackLexiconId: 631,
              }),
              buildMetric(158, {
                preferredText: "Ball Recoveries",
                fallbackLexiconId: 337,
              }),
              defensiveMistakesMetric,
            ],
            labels: {
              primary: {
                preferredText: "Duels",
                fallbackLexiconId: 20,
              },
              secondaryById: {
                152: { preferredText: "Interceptions", fallbackLexiconId: 635 },
                143: { preferredText: "Tackles Won", fallbackLexiconId: 631 },
                158: { preferredText: "Ball Recoveries", fallbackLexiconId: 337 },
              },
            },
          },
          buildCard({
            key: "of-aerial-duels-plus",
            tone: "defense",
            primaryId: 128,
            secondaryIds: [142, 21, 23, 159],
            secondaryLabels: {
              142: { preferredText: "Tackles Made", fallbackLexiconId: 630 },
              21: { preferredText: "Yellow Cards", fallbackLexiconId: 104 },
              23: { preferredText: "Red Cards", fallbackLexiconId: 103 },
              159: { preferredText: "Ball Losses", fallbackLexiconId: 336 },
            },
            primaryLabel: {
              preferredLexiconId: 615,
              fallbackText: "Aerial Duels +",
            },
          }),
        ],
      },
    ];
  }, [
    aggregatedMatchStatistics,
    getParameterById,
    getPlayerParameterLexiconId,
    isGoalkeeper,
    playerParameterIdByLexicon,
    resolveBottomLabel,
  ]);

  const filteredMatchesByHeader = useMemo(() => {
    const matchesList = Array.isArray(players?.matches_list) ? players.matches_list : [];
    if (selectedMatchIds.length === 0) {
      return [];
    }

    const selectedIdSet = new Set(selectedMatchIds.map((matchId) => Number(matchId)));
    return matchesList.filter((match) => selectedIdSet.has(Number(match?.id)));
  }, [players?.matches_list, selectedMatchIds]);

  // Fallback shown while a code-split tab panel chunk is being fetched.
  const tabPanelFallback = (
    <LoadingWrapper>
      <Oval
        visible={true}
        height="80"
        width="80"
        secondaryColor={
          localStorage.getItem("theme") === "light" ? "#4E5255" : "#CFDAE6"
        }
        color={
          localStorage.getItem("theme") === "light" ? "#2363F7" : "#4fa94d"
        }
        ariaLabel="oval-loading"
        wrapperStyle={{}}
        wrapperClass=""
      />
    </LoadingWrapper>
  );

  return (
    <MainWrapper>
      <Navigation />
      <MainWrapperFavorite
        className={
          localStorage.getItem("navigationOpen") === "true"
            ? ""
            : "fullWidthPage"
        }
        style={accessDenied ? { overflow: 'hidden' } : undefined}
      >
        <TeamPageContainer>
        {accessDenied && (
          <AccessDenied>
            {l(1119)}
          </AccessDenied>
        )}
        <TopWrapper>
          <IntroText smallTitle="Hello John" title={l(1003)} />
          <Search />
        </TopWrapper>

        {showPageLoader ? (
          <LoadingWrapper>
            <Oval
              visible={true}
              height="80"
              width="80"
              secondaryColor={
                localStorage.getItem("theme") === "light"
                  ? "#4E5255"
                  : "#CFDAE6"
              }
              color={
                localStorage.getItem("theme") === "light"
                  ? "#2363F7"
                  : "#4fa94d"
              }
              ariaLabel="oval-loading"
              wrapperStyle={{}}
              wrapperClass=""
            />
          </LoadingWrapper>
        ) : (
          <>
            <Breadcrumbs items={breadcrumbs} />

            <TeamCardWrap>
              <TeamContent>
                <TopContent>
                  <Info>
                    <AvatarWithCountryContainer>
                      <img
                        src={
                          players?.player?.photos.length > 0
                            ? players?.player?.photos[0]?.full ?? emptyState
                            : emptyState
                        }
                        alt={players?.team?.logo}
                      />
                      {players?.player?.nationality?.[0]?.flag !== null ? (
                        <img
                          className="flag"
                          src={getMediaUrl(players?.player?.nationality?.[0]?.flag)}
                          alt={players?.player?.nationality?.[0]?.name}
                        />
                      ) : (
                        <img
                          className="flag"
                          src={getMediaUrl(players?.player?.team.logo)}
                          alt={players?.player?.team.logo}
                        />
                      )}
                    </AvatarWithCountryContainer>

                    <TeamLeftInfo>
                      {players?.player?.nationality?.length > 0 && (
                        <h5>{players?.player?.nationality?.[0]?.short_name}</h5>
                      )}
                      <h2>
                        {players?.player?.display_name}
                        <img
                          src={isFavorite ? starOn : starOff}
                          alt="Favorite star"
                          style={{
                            width: "18px",
                            height: "100%",
                            cursor: "pointer",
                          }}
                          onClick={toggleFavorite}
                        />
                      </h2>

                      <h3
                        data-tooltip-id={`position-${players?.player?.position[0]?.name}}`}
                      >
                        {players?.player?.position[0]?.name}
                      </h3>

                      <ReactTooltip
                        id={`position-${players?.player?.position[0]?.name}}`}
                        place="bottom"
                        content={players?.player?.position.map(
                          (item, index) => (
                            <h3 key={index}>{item.name}</h3>
                          )
                        )}
                      />
                    </TeamLeftInfo>
                  </Info>

                  <PlayerHeaderFilters>
                    <PlayerHeaderFilterField>
                      <PlayerHeaderFilterLabel>{l(453)}</PlayerHeaderFilterLabel>
                      <Dropdown
                        ref={headerSeasonDropdownRef}
                        disabled={!availableSeasons.length}
                      >
                        <PlayerHeaderDropdownButton
                          onClick={() =>
                            setOpenHeaderSeasonDropdown((prev) => !prev)
                          }
                        >
                          <PlayerHeaderDropdownText>
                            {selectedHeaderSeasonLabel}
                          </PlayerHeaderDropdownText>
                          <img
                            src={
                              openHeaderSeasonDropdown ? arrowIconUp : arrowIconDown
                            }
                            alt="toggle"
                          />
                        </PlayerHeaderDropdownButton>
                        <DropdownContent $show={openHeaderSeasonDropdown}>
                          {availableSeasons.length > 0 ? (
                            <>
                              <MatchDropdownItem
                                selected={isAllAvailableSeasonsSelected}
                                onClick={toggleAllAvailableSeasons}
                              >
                                <MatchCheckbox
                                  type="checkbox"
                                  readOnly
                                  checked={isAllAvailableSeasonsSelected}
                                />
                                {t("playerReport.allSeasons")}
                              </MatchDropdownItem>
                              {availableSeasons.map((seasonItem) => (
                                <MatchDropdownItem
                                  key={seasonItem?.id}
                                  selected={selectedSeasonIds.includes(
                                    Number(seasonItem?.id)
                                  )}
                                  onClick={() => toggleSelectedSeason(seasonItem?.id)}
                                >
                                  <MatchCheckbox
                                    type="checkbox"
                                    readOnly
                                    checked={selectedSeasonIds.includes(
                                      Number(seasonItem?.id)
                                    )}
                                  />
                                  {seasonItem?.year_season?.years || seasonItem?.year_season || seasonItem?.years || seasonItem?.id}
                                </MatchDropdownItem>
                              ))}
                            </>
                          ) : (
                            <DropdownItem>{l(464)}</DropdownItem>
                          )}
                        </DropdownContent>
                      </Dropdown>
                    </PlayerHeaderFilterField>

                    <PlayerHeaderFilterField>
                      <PlayerHeaderFilterLabel>{l(454)}</PlayerHeaderFilterLabel>
                      <Dropdown
                        ref={headerCompetitionDropdownRef}
                        disabled={
                          selectedSeasonItems.length === 0 ||
                          competitions.length === 0
                        }
                      >
                        <PlayerHeaderDropdownButton
                          onClick={() =>
                            setOpenHeaderCompetitionDropdown((prev) => !prev)
                          }
                        >
                          <PlayerHeaderDropdownText>
                            {selectedHeaderCompetitionLabel}
                          </PlayerHeaderDropdownText>
                          <img
                            src={
                              openHeaderCompetitionDropdown
                                ? arrowIconUp
                                : arrowIconDown
                            }
                            alt="toggle"
                          />
                        </PlayerHeaderDropdownButton>
                        <DropdownContent $show={openHeaderCompetitionDropdown}>
                          {competitions.length > 0 ? (
                            competitions.map((competitionItem) => (
                              <MatchDropdownItem
                                key={competitionItem?.competition_id}
                                selected={selectedCompetition.some(
                                  (selectedItem) =>
                                    Number(selectedItem?.competition_id) ===
                                    Number(competitionItem?.competition_id)
                                )}
                                onClick={() => {
                                  setHasCustomCompetitionSelection(true);
                                  setSelectedCompetition((prevCompetition) => {
                                    const isSelected = prevCompetition.some(
                                      (selectedItem) =>
                                        Number(selectedItem?.competition_id) ===
                                        Number(competitionItem?.competition_id)
                                    );

                                    if (isSelected) {
                                      return prevCompetition.filter(
                                        (selectedItem) =>
                                          Number(selectedItem?.competition_id) !==
                                          Number(competitionItem?.competition_id)
                                      );
                                    }

                                    return [...prevCompetition, competitionItem];
                                  });
                                }}
                              >
                                <MatchCheckbox
                                  type="checkbox"
                                  readOnly
                                  checked={selectedCompetition.some(
                                    (selectedItem) =>
                                      Number(selectedItem?.competition_id) ===
                                      Number(competitionItem?.competition_id)
                                  )}
                                />
                                {competitionItem?.competition}
                              </MatchDropdownItem>
                            ))
                          ) : (
                            <DropdownItem>{l(464)}</DropdownItem>
                          )}
                        </DropdownContent>
                      </Dropdown>
                    </PlayerHeaderFilterField>

                    <PlayerHeaderFilterField>
                      <PlayerHeaderFilterLabel>{l(7)}</PlayerHeaderFilterLabel>
                      <Dropdown
                        ref={headerMatchesDropdownRef}
                        disabled={
                          selectedSeasonItems.length === 0 ||
                          competitions.length === 0 ||
                          selectedCompetition.length === 0
                        }
                      >
                        <PlayerHeaderDropdownButton
                          onClick={() =>
                            setOpenHeaderMatchesDropdown((prev) => !prev)
                          }
                        >
                          <PlayerHeaderDropdownText>
                            {selectedHeaderMatchesLabel}
                          </PlayerHeaderDropdownText>
                          <img
                            src={openHeaderMatchesDropdown ? arrowIconUp : arrowIconDown}
                            alt="toggle"
                          />
                        </PlayerHeaderDropdownButton>
                        <DropdownContent $show={openHeaderMatchesDropdown}>
                          {matchesCountList.map((option) => (
                            <DropdownItem
                              key={option?.id}
                              selected={Number(selectedMatchCount?.id) === Number(option?.id)}
                              onClick={() => {
                                setSelectedMatchCount(option);
                                setOpenHeaderMatchesDropdown(false);
                              }}
                            >
                              {option?.title}
                            </DropdownItem>
                          ))}
                        </DropdownContent>
                      </Dropdown>
                    </PlayerHeaderFilterField>
                  </PlayerHeaderFilters>

                  {isMobile && (
                    <BottomContent>
                      <TeamStatistics style={{ padding: 0 }}>
                        <Stat>
                          <div>
                            <h5>{l(1004)}</h5>
                            <h4
                              style={{ cursor: players?.player?.team?.id ? 'pointer' : 'not-allowed' }}
                              onClick={() => navigate(`/tournaments/team/${players?.player?.team?.id}`)}
                            >
                              {players?.player?.team?.name}
                            </h4>
                          </div>
                          {/* <div>
                            <h5>Past Club</h5>
                            <h4
                              style={{ cursor: players?.player?.teams?.[1]?.id ? 'pointer' : 'not-allowed' }}
                              onClick={() => navigate(`/tournaments/team/${players?.player?.teams?.[1]?.id}`)}
                            >
                              {players?.player?.teams?.[1]?.name ?? "/"}
                            </h4>
                          </div> */}
                          <div>
                            <h5>{l(115)}</h5>
                            <h4>
                              {" "}
                              {players?.player?.position[1]?.name ?? "/"}
                            </h4>
                          </div>
                          <div>
                            <h5>{l(112)}</h5>
                            <h4>{players?.player?.leg ?? "/"}</h4>
                          </div>
                          <div>
                            <h5>{l(327)}</h5>
                            <h4>
                              {players?.player?.birth_date
                                ? new Date().getFullYear() -
                                new Date(
                                  players.player.birth_date
                                ).getFullYear() -
                                (new Date().getMonth() <
                                  new Date(
                                    players.player.birth_date
                                  ).getMonth() ||
                                  (new Date().getMonth() ===
                                    new Date(
                                      players.player.birth_date
                                    ).getMonth() &&
                                    new Date().getDate() <
                                    new Date(
                                      players.player.birth_date
                                    ).getDate())
                                  ? 1
                                  : 0)
                                : "/"}
                            </h4>
                          </div>
                          <div>
                            <h5>{l(113)}</h5>
                            <h4>{`${players?.player?.height} cm` ?? "/"}</h4>
                          </div>
                          <div>
                            <h5>{l(1006)}</h5>
                            <h4>{players?.player?.weight ?? "/"}</h4>
                          </div>
                          <div>
                            <h5>{l(1007)}</h5>
                            <ButtonLink
                              title={l(1008)}
                              underline
                              color="primary"
                              size="small"
                              onClick={() => handleCompare()}
                            />
                          </div>
                        </Stat>
                      </TeamStatistics>
                    </BottomContent>
                  )}
                  <TeamButtons>
                    <Button
                      color={"secondary"}
                      onClick={() => handleScreenShot()}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z"
                        />
                      </svg>
                    </Button>
                    <ShadowTeamDropdownWrap ref={shadowTeamDropdownRef}>
                      <ShadowTeamDropdownBtn
                        $open={shadowTeamDropdownOpen}
                        disabled={addingToShadowTeam}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenShadowTeamDropdown();
                        }}
                      >
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="8"
                            cy="8"
                            r="6.5"
                            stroke="currentColor"
                            strokeWidth="1.4"
                          />
                          <path
                            d="M8 5v6M5 8h6"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                          />
                        </svg>
                        {addingToShadowTeam
                          ? "Adding..."
                          : "Add to Shadow Team"}
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{
                            transform: shadowTeamDropdownOpen
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.15s",
                          }}
                        >
                          <path
                            d="M2 3.5l3 3 3-3"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </ShadowTeamDropdownBtn>

                      {shadowTeamDropdownOpen && (
                        <ShadowTeamPanel onClick={(e) => e.stopPropagation()}>
                          {!shadowTeamSelectedTeamId ? (
                            <>
                              <ShadowTeamPanelLabel>
                                Select shadow team
                              </ShadowTeamPanelLabel>
                              {shadowTeams.length === 0 ? (
                                <ShadowTeamEmptyMsg>
                                  No shadow teams yet
                                </ShadowTeamEmptyMsg>
                              ) : (
                                shadowTeams.map((team) => (
                                  <ShadowTeamRow
                                    key={team.id}
                                    onClick={() =>
                                      handleSelectShadowTeam(team)
                                    }
                                  >
                                    <div>
                                      <ShadowTeamRowName>
                                        {team.name}
                                      </ShadowTeamRowName>
                                      {team.formationName && (
                                        <ShadowTeamRowMeta>
                                          {team.formationName}
                                        </ShadowTeamRowMeta>
                                      )}
                                    </div>
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 14 14"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                      style={{ flexShrink: 0, opacity: 0.4 }}
                                    >
                                      <path
                                        d="M5 3l4 4-4 4"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </ShadowTeamRow>
                                ))
                              )}
                              <ShadowTeamDivider />
                              <ShadowTeamNewRow
                                onClick={() => {
                                  setShadowTeamDropdownOpen(false);
                                  window.location.href = "/shadow-team";
                                }}
                              >
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M6 1v10M1 6h10"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                                New shadow team
                              </ShadowTeamNewRow>
                            </>
                          ) : (
                            <>
                                <ShadowTeamPanelBack
                                  onClick={() => {
                                    setShadowTeamSelectedTeamId(null);
                                    setShadowTeamLoadingTeamId(null);
                                  }}
                                >
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M8 2L4 6l4 4"
                                    stroke="currentColor"
                                    strokeWidth="1.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                Back
                              </ShadowTeamPanelBack>
                              <ShadowTeamPanelStepTitle>
                                {selectedShadowTeam?.name}
                              </ShadowTeamPanelStepTitle>
                              {getShadowTeamSlotGroups(
                                selectedShadowTeam?.formation
                              ).map((group) => (
                                <ShadowTeamSlotSection key={group.lineIndex}>
                                  <ShadowTeamSlotSectionLabel>
                                    {SHADOW_BAND_LABELS[group.band] ||
                                      group.band}
                                  </ShadowTeamSlotSectionLabel>
                                  <ShadowTeamSlotGrid>
                                    {group.slots.map((slot) => {
                                      const isSlotDisabled =
                                        addingToShadowTeam ||
                                        isShadowTeamDetailLoading ||
                                        !selectedShadowTeamHasLoadedAssignments ||
                                        isShadowTeamSlotFull(slot);

                                      return (
                                        <ShadowTeamSlotPill
                                          key={slot.key}
                                          disabled={isSlotDisabled}
                                          onClick={() =>
                                            handleAddToShadowTeamSlot(slot)
                                          }
                                        >
                                          {slot.shortLabel}
                                        </ShadowTeamSlotPill>
                                      );
                                    })}
                                  </ShadowTeamSlotGrid>
                                </ShadowTeamSlotSection>
                              ))}
                            </>
                          )}
                        </ShadowTeamPanel>
                      )}
                    </ShadowTeamDropdownWrap>
                  </TeamButtons>
                </TopContent>
                {!isMobile && (
                  <BottomContent>
                    <TeamStatistics>
                      <Stat>
                        <div>
                          <h5>{l(1004)}</h5>
                          <h4
                            style={{ cursor: players?.player?.team?.id ? 'pointer' : 'not-allowed' }}
                            onClick={() => navigate(`/tournaments/team/${players?.player?.team?.id}`)}
                          >
                            {players?.player?.team?.name}
                          </h4>
                        </div>
                        {/* <div>
                          <h5>Past Club</h5>
                          <h4
                            style={{ cursor: players?.player?.teams?.[1]?.id ? 'pointer' : 'not-allowed' }}
                            onClick={() => navigate(`/tournaments/team/${players?.player?.teams?.[1]?.id}`)}
                          >
                            {" "}
                            {players?.player?.teams[1]?.name ?? "/"}
                          </h4>
                        </div> */}
                        <div>
                          <h5>{l(115)}</h5>
                          <h4 style={{ textAlign: "center" }}>
                            {players?.player?.position[1]?.name ?? "/"}
                          </h4>
                        </div>
                        <div>
                          <h5>{l(112)}</h5>
                          <h4>{players?.player?.leg ?? "/"}</h4>
                        </div>
                        <div>
                          <h5>{l(327)}</h5>
                          <h4>
                            {players?.player?.birth_date
                              ? new Date().getFullYear() -
                              new Date(
                                players.player.birth_date
                              ).getFullYear() -
                              (new Date().getMonth() <
                                new Date(
                                  players.player.birth_date
                                ).getMonth() ||
                                (new Date().getMonth() ===
                                  new Date(
                                    players.player.birth_date
                                  ).getMonth() &&
                                  new Date().getDate() <
                                  new Date(
                                    players.player.birth_date
                                  ).getDate())
                                ? 1
                                : 0)
                              : "/"}
                          </h4>
                        </div>
                        <div>
                          <h5>{l(113)}</h5>
                          <h4>{`${players?.player?.height} cm` ?? "/"}</h4>
                        </div>
                        <div>
                          <h5>{l(1006)}</h5>

                          <h4>{`${players?.player?.weight} kg` ?? "/"}</h4>
                        </div>
                        <div>
                          <h5>{l(1007)}</h5>
                          <ButtonLink
                            title={l(1008)}
                            underline
                            color="primary"
                            size="small"
                            onClick={() => handleCompare()}
                          />
                        </div>
                      </Stat>
                    </TeamStatistics>
                  </BottomContent>
                )}
              </TeamContent>
            </TeamCardWrap>

            <TabsWrapper
              selectedIndex={selectedTab}
              onSelect={handleTabSelect}
            >
              <TopList>
                <TabListWrapper>
                  <TabTitle>{l(988)}</TabTitle>
                  <TabTitle>{l(7)}</TabTitle>
                  <TabTitle>{l(8)}</TabTitle>
                  <TabTitle>{l(9)}</TabTitle>
                  <TabTitle>{l(18)}</TabTitle>
                  <TabTitle>{l(587)}</TabTitle>
                </TabListWrapper>
                {canDownloadReport === true && (
                  <Button
                    disabled={!id || isDownloadingReport}
                    onClick={handleDownloadPlayerReport}
                    title={isDownloadingReport ? l(312) : "Download Report"}
                    color={"secondary"}
                    style={{ whiteSpace: "nowrap", alignSelf: "center" }}
                  />
                )}
              </TopList>

              <TabContent hidden={true}>
                <>
                  <TopCardsWrap>
                    <WrappContent>
                      <TopCardInfo>
                        <h2>{l(8)}</h2>
                        <ToolbarSpacer />
                      </TopCardInfo>
                      <OverviewCard style={{ height: 400 }}>
                        <InnerContent>
                          {overviewStatsDisplayItems.length > 0 ? (
                            overviewStatsDisplayItems.map(({ parameter, displayName }) => {
                              return (
                                <div
                                  key={parameter?.parameter_id}
                                  className="statistics statsPadding"
                                >
                                  <h5
                                    title={displayName}
                                    style={{ maxWidth: "calc(100% - 72px)" }}
                                  >
                                    {displayName}:
                                  </h5>
                                  <h4
                                    style={
                                      canOpenPlayerStat(parameter)
                                        ? { cursor: "pointer" }
                                        : { cursor: "not-allowed" }
                                    }
                                    onClick={() => openPlayerStatVideos(parameter)}
                                    onContextMenu={() => openPlayerStatVideos(parameter)}
                                  >
                                    {formatStatCount(getOverviewMetricDisplayCount(parameter))}
                                  </h4>
                                </div>
                              );
                            })
                          ) : (
                            <EmptyCard>
                              <h2>{l(464)}</h2>
                            </EmptyCard>
                          )}
                        </InnerContent>
                      </OverviewCard>
                    </WrappContent>
                    <Matches
                      table={filteredMatchesByHeader}
                      onChangeData={(data) => setPlayers(data)}
                      season={primarySelectedSeasonId}
                      playerTeams={playerMatches?.player?.teams || []}
                      showTitle={true}
                      hideFilterButton
                      controlled
                      playerId={id}
                      styleTableWrapper={{
                        height: "400px",
                        overflowY: "scroll",
                        scrollbarWidth: "auto",
                      }}
                    />
                  </TopCardsWrap>

                  <BottomCardsWrap>
                    {overviewVideoSections.map((section) => (
                      <WrappBottomContent key={section.key}>
                        <TopCardInfo>
                          <h2>{`${resolveBottomLabel(section.titleLabel, section.title)}: ${l(1061)}`}</h2>
                        </TopCardInfo>
                        <OverviewCard
                          style={{ height: "fit-content", scrollbarWidth: "none" }}
                        >
                          <InnerContent>
                            <VideoContent>
                              {section.cards.map((card) => {
                                const metrics = card.group.filter(Boolean);
                                const [primaryMetric, ...secondaryMetrics] = metrics;
                                if (!primaryMetric) return null;

                                const isDefenseCard = card.tone === "defense";
                                const isLightTheme =
                                  localStorage.getItem("theme") === "light";

                                return (
                                  <VideoStats key={card.key}>
                                    <StatsCard
                                      className={isDefenseCard ? "defense" : ""}
                                      style={{
                                        cursor: canOpenPlayerStat(primaryMetric)
                                          ? "pointer"
                                          : "not-allowed",
                                      }}
                                      onClick={() =>
                                        openPlayerStatVideos(
                                          primaryMetric,
                                          false,
                                          card?.labels?.primary
                                        )
                                      }
                                      onContextMenu={() =>
                                        openPlayerStatVideos(
                                          primaryMetric,
                                          false,
                                          card?.labels?.primary
                                        )
                                      }
                                    >
                                      <h4
                                        style={{
                                          color: isLightTheme ? "#015ead" : "#fff",
                                        }}
                                      >
                                        {formatStatCount(
                                          getOverviewMetricDisplayCount(primaryMetric)
                                        )}
                                      </h4>
                                      <h5
                                        style={{
                                          color: isLightTheme
                                            ? "#015ead"
                                            : isDefenseCard
                                              ? "#F20A46"
                                              : "#36e82d",
                                        }}
                                      >
                                        {getBottomMetricLabel(primaryMetric, card?.labels?.primary)}
                                      </h5>
                                      <img
                                        src={isLightTheme ? playBlue : play}
                                        width={40}
                                        alt={play}
                                        style={{
                                          position: "absolute",
                                          right: 20,
                                          top: "10%",
                                        }}
                                      />
                                    </StatsCard>

                                    <VideoList>
                                      {secondaryMetrics.map((metric) => (
                                        <VideoItems
                                          key={`${card.key}-${metric.parameter_id}`}
                                          style={{
                                            cursor: canOpenPlayerStat(metric)
                                              ? "pointer"
                                              : "not-allowed",
                                          }}
                                          onClick={() =>
                                            openPlayerStatVideos(
                                              metric,
                                              false,
                                              card?.labels?.secondaryById?.[metric.parameter_id]
                                            )
                                          }
                                          onContextMenu={() =>
                                            openPlayerStatVideos(
                                              metric,
                                              false,
                                              card?.labels?.secondaryById?.[metric.parameter_id]
                                            )
                                          }
                                        >
                                          <img
                                            src={isLightTheme ? playBlue : play}
                                            alt={play}
                                          />
                                          <VideoInfo>
                                            <h3>
                                              {getBottomMetricLabel(
                                                metric,
                                                card?.labels?.secondaryById?.[metric.parameter_id]
                                              )}
                                            </h3>
                                            <span>
                                              {formatStatCount(
                                                getOverviewMetricDisplayCount(metric)
                                              )}{" "}
                                              {l(1061)}
                                            </span>
                                          </VideoInfo>
                                        </VideoItems>
                                      ))}
                                    </VideoList>
                                  </VideoStats>
                                );
                              })}
                              {section.cards.every((card) => card.group.filter(Boolean).length === 0) && (
                                <EmptyCard>
                                  <h2>{l(464)}</h2>
                                </EmptyCard>
                              )}
                            </VideoContent>
                          </InnerContent>
                        </OverviewCard>
                      </WrappBottomContent>
                    ))}
                  </BottomCardsWrap>
                </>
              </TabContent>
              <TabContent hidden={true}>
                <Matches
                  table={filteredMatchesByHeader}
                  onChangeData={(data) => setPlayers(data)}
                  season={primarySelectedSeasonId}
                  playerTeams={playerMatches?.player?.teams || []}
                  hideFilterButton
                  controlled
                  showClickableStatsHint
                  playerId={id}
                />
              </TabContent>
              <TabContent hidden={true}>
                <PlayerStat
                  statistics={getPlayerInfoStat}
                  loadingStat={loading}
                  mainStat={players}
                  season={selectedSeasonQueryValue || primarySelectedSeasonId}
                  onDataChange={(season, params, downloadType) =>
                    getPlayerNew(id, season, params, downloadType)
                  }
                  tempSelectedFilters={tempSelectedFilters}
                  selectedMatchIds={selectedMatchIds}
                  onChangeParameters={handlePlayerParametersChange}
                  savedTemplatesState={savedTemplates}
                  playerId={id}
                />
              </TabContent>
              <TabContent hidden={true}>
                <Suspense fallback={tabPanelFallback}>
                  <EventsPlayer
                    key={selectedTab === 3 ? `events-player-${selectedTab}` : 'events-player'}
                    players={players}
                    paramsList={paramsList}
                    user={userDetail}
                    selectedMatchIds={selectedMatchIds}
                    selectedMatches={selectedMatches}
                    isActive={selectedTab === 3}
                  />
                </Suspense>
              </TabContent>
              <TabContent hidden={true}>
                <Suspense fallback={tabPanelFallback}>
                  <ShotsPlayer
                    players={players}
                    paramsList={paramsList}
                    paramsShotList={paramsShotList}
                    selectedMatchIds={selectedMatchIds}
                  />
                </Suspense>
              </TabContent>
              <TabContent hidden={true}>
                <Suspense fallback={tabPanelFallback}>
                  <CrossesPlayer
                    players={players}
                    paramsList={paramsList}
                    paramsShotList={paramsCrossesList}
                    selectedMatchIds={selectedMatchIds}
                  />
                </Suspense>
              </TabContent>
            </TabsWrapper>
          </>
        )}
        </TeamPageContainer>
      </MainWrapperFavorite>
      {modal}

      {/* display notifications */}
      <NotificationList
        notifications={notifications}
        onClose={removeNotification}
      />
    </MainWrapper>
  );
};

export default Index;
